import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../utils/api';
import { Download, FileText, Eye, Trash2, X, ExternalLink, Edit } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const ICON_SIZE_OPTIONS = [
  { value: 'XS', label: 'XS', hint: 'Compact' },
  { value: 'S', label: 'S', hint: 'Standard' },
  { value: 'M', label: 'M', hint: 'Detaille' },
  { value: 'L', label: 'L', hint: 'Grand' },
];
const STORAGE_KEY_ICON_SIZE = 'presthyg_pdf_icon_size';

const Plans = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfPreview, setPdfPreview] = useState(null); // { url, planName }
  const [pdfLoading, setPdfLoading] = useState(false);
  const [iconSize, setIconSize] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_ICON_SIZE);
      return ICON_SIZE_OPTIONS.some(o => o.value === stored) ? stored : 'S';
    } catch {
      return 'S';
    }
  });

  const handleChangeIconSize = (size) => {
    setIconSize(size);
    try { localStorage.setItem(STORAGE_KEY_ICON_SIZE, size); } catch {}
    // If a preview is open, regenerate it with the new size
    if (pdfPreview) {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const baseUrl = BACKEND_BASE;
      // Extract plan id from current preview URL
      const match = pdfPreview.url.match(/\/plans\/([^/]+)\/pdf/);
      if (match) {
        const planId = match[1];
        const newUrl = `${baseUrl}/api/plans/${planId}/pdf?mode=preview&icon_size=${size}&token=${encodeURIComponent(token)}`;
        setPdfLoading(true);
        setPdfPreview({ ...pdfPreview, url: newUrl });
        setTimeout(() => setPdfLoading(false), 2500);
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, clientsRes] = await Promise.all([api.get('/plans'), api.get('/clients')]);
      setPlans(plansRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Echec du chargement des plans');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPDF = (planId, planName) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error('Session expiree. Veuillez vous reconnecter.');
      return;
    }
    const baseUrl = BACKEND_BASE;
    const pdfUrl = `${baseUrl}/api/plans/${planId}/pdf?mode=preview&icon_size=${iconSize}&token=${encodeURIComponent(token)}`;
    setPdfLoading(true);
    setPdfPreview({ url: pdfUrl, planName: planName || 'Plan HACCP' });
    // Fallback: iframe onLoad is unreliable for PDF blobs in some browsers,
    // so force-hide the spinner after 2.5s.
    setTimeout(() => setPdfLoading(false), 2500);
  };

  const handleDownloadPDF = (planId, planName) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error('Session expiree. Veuillez vous reconnecter.');
      return;
    }
    const baseUrl = BACKEND_BASE;
    const pdfUrl = `${baseUrl}/api/plans/${planId}/pdf?mode=download&icon_size=${iconSize}&token=${encodeURIComponent(token)}`;
    const link = document.createElement('a');
    link.href = pdfUrl;
    const safeName = (planName || 'plan_haccp').replace(/[^a-zA-Z0-9_\- ]/g, '_').trim() || 'plan_haccp';
    link.setAttribute('download', `${safeName}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Telechargement lance');
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Supprimer ce plan ?')) return;
    try {
      await api.delete(`/plans/${planId}`);
      toast.success('Plan supprime');
      loadData();
    } catch (error) {
      toast.error('Echec de la suppression');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Chargement...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="plans-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
            Plans HACCP
          </h1>
          <Link
            to="/plan-builder"
            data-testid="new-plan-button"
            className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
            style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}
          >
            <FileText size={20} />
            <span>Nouveau Plan</span>
          </Link>
        </div>

        {/* PDF icon size selector */}
        <div className="mb-5 p-4 rounded-lg border flex items-center justify-between gap-4 flex-wrap"
          style={{ borderColor: '#E5E7EB', backgroundColor: '#FAFAFA' }}
          data-testid="pdf-icon-size-panel">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm font-semibold" style={{ color: '#0E2D43' }}>
              Taille des icones PDF
            </label>
            <div className="inline-flex rounded-md border overflow-hidden"
              style={{ borderColor: '#D1D5DB' }}
              role="radiogroup"
              aria-label="Taille des icones PDF">
              {ICON_SIZE_OPTIONS.map((opt) => {
                const active = iconSize === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-testid={`icon-size-${opt.value}`}
                    onClick={() => handleChangeIconSize(opt.value)}
                    className="px-4 py-1.5 text-sm font-semibold transition-colors"
                    style={{
                      backgroundColor: active ? '#0E2D43' : '#FFFFFF',
                      color: active ? '#FFFFFF' : '#0E2D43',
                      borderRight: opt.value !== 'L' ? '1px solid #D1D5DB' : 'none',
                      minWidth: '48px',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <span className="text-xs" style={{ color: '#6B7280' }}>
              {ICON_SIZE_OPTIONS.find(o => o.value === iconSize)?.hint}
            </span>
          </div>
          <p className="text-xs" style={{ color: '#6B7280' }}>
            S'applique a la <strong>Preview</strong> et au <strong>Telechargement</strong>. XS = compact, L = affichage grand.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow border" style={{ borderColor: '#E5E7EB' }}>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: '1000px' }}>
              <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium" style={{ width: '25%' }}>Nom du Plan</th>
                  <th className="px-6 py-3 text-left text-sm font-medium" style={{ width: '20%' }}>Client</th>
                  <th className="px-6 py-3 text-left text-sm font-medium" style={{ width: '10%' }}>Statut</th>
                  <th className="px-6 py-3 text-left text-sm font-medium" style={{ width: '15%' }}>Date</th>
                  <th className="px-6 py-3 text-right text-sm font-medium" style={{ width: '30%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center" style={{ color: '#6B7280' }}>
                      Aucun plan pour le moment. Creez votre premier plan !
                    </td>
                  </tr>
                ) : (
                  plans.map((plan) => {
                    const client = clients.find((c) => c.id === plan.client_id);
                    return (
                      <tr key={plan.id} data-testid={`plan-row-${plan.id}`} className="border-t" style={{ borderColor: '#E5E7EB' }}>
                        <td className="px-6 py-4 font-medium" style={{ color: '#0E2D43' }}>
                          {plan.plan_name}
                        </td>
                        <td className="px-6 py-4" style={{ color: '#6B7280' }}>
                          {client?.name || 'Inconnu'}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className="px-3 py-1 rounded text-sm"
                            style={{
                              backgroundColor: plan.status === 'draft' ? '#FEF3C7' : '#D1FAE5',
                              color: plan.status === 'draft' ? '#92400E' : '#065F46',
                            }}
                          >
                            {plan.status === 'draft' ? 'Brouillon' : plan.status}
                          </span>
                        </td>
                        <td className="px-6 py-4" style={{ color: '#6B7280' }}>
                          {new Date(plan.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/plans/${plan.id}/edit`)}
                              data-testid={`edit-plan-${plan.id}`}
                              className="inline-flex items-center space-x-1 px-3 py-2 rounded font-medium text-sm"
                              style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                            >
                              <Edit size={16} />
                              <span>Editer</span>
                            </button>
                            <button
                              onClick={() => handleViewPDF(plan.id, plan.plan_name)}
                              data-testid={`view-pdf-${plan.id}`}
                              className="inline-flex items-center space-x-1 px-3 py-2 rounded font-medium text-sm"
                              style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}
                            >
                              <Eye size={16} />
                              <span>Voir PDF</span>
                            </button>
                            <button
                              onClick={() => handleDownloadPDF(plan.id, plan.plan_name)}
                              data-testid={`download-pdf-${plan.id}`}
                              className="inline-flex items-center space-x-1 px-3 py-2 rounded font-medium text-sm"
                              style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}
                            >
                              <Download size={16} />
                              <span>Telecharger</span>
                            </button>
                            <button
                              onClick={() => handleDelete(plan.id)}
                              data-testid={`delete-plan-${plan.id}`}
                              className="inline-flex items-center px-3 py-2 rounded text-sm"
                              style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PDF Preview Modal */}
        {pdfPreview && (
          <div
            data-testid="pdf-preview-modal"
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          >
            <div className="relative bg-white rounded-lg shadow-2xl" style={{ width: '95vw', height: '92vh' }}>
              {/* Modal Header */}
              <div
                className="flex items-center justify-between px-5 py-3 rounded-t-lg"
                style={{ backgroundColor: '#0E2D43' }}
              >
                <h3 className="text-white font-semibold text-base truncate" style={{ maxWidth: '40%' }}>
                  {pdfPreview.planName}
                </h3>
                <div className="flex items-center gap-3">
                  {/* Icon size switch inside preview */}
                  <div className="inline-flex rounded border overflow-hidden"
                    style={{ borderColor: 'rgba(255,255,255,0.25)' }}
                    data-testid="preview-icon-size-switch">
                    {ICON_SIZE_OPTIONS.map((opt) => {
                      const active = iconSize === opt.value;
                      return (
                        <button key={opt.value}
                          type="button"
                          data-testid={`preview-icon-size-${opt.value}`}
                          onClick={() => handleChangeIconSize(opt.value)}
                          className="px-2.5 py-1 text-xs font-semibold transition-colors"
                          style={{
                            backgroundColor: active ? '#FFFFFF' : 'transparent',
                            color: active ? '#0E2D43' : '#FFFFFF',
                            borderRight: opt.value !== 'L' ? '1px solid rgba(255,255,255,0.2)' : 'none',
                          }}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <a
                    href={pdfPreview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pdf-open-new-tab"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium"
                    style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#FFFFFF' }}
                  >
                    <ExternalLink size={14} />
                    <span>Nouvel onglet</span>
                  </a>
                  <button
                    onClick={() => { setPdfPreview(null); setPdfLoading(false); }}
                    data-testid="pdf-close-modal"
                    className="p-1.5 rounded hover:bg-white/20 transition-colors"
                    style={{ color: '#FFFFFF' }}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              {/* PDF iframe */}
              {pdfLoading && (
                <div className="absolute inset-0 mt-[52px] flex items-center justify-center rounded-b-lg" style={{ backgroundColor: '#F8FAFC' }}>
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-3" style={{ borderColor: '#1D4ED8' }}></div>
                    <p className="text-sm" style={{ color: '#64748B' }}>Chargement du PDF...</p>
                  </div>
                </div>
              )}
              <iframe
                src={pdfPreview.url}
                data-testid="pdf-iframe"
                title="Apercu PDF"
                className="w-full rounded-b-lg"
                style={{ height: 'calc(92vh - 52px)', border: 'none' }}
                onLoad={() => setPdfLoading(false)}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Plans;
